import React from "react";
import { Edit, Trash2, Download, DollarSign } from "lucide-react";
import { ProfitEntry } from "../../../../services/profitTrackingService";
import { formatNumber } from "../utils/profitFormatters";
import { computeFinalProfits, expenseToCadXaf } from "../hooks/useProfitCalculations";

interface EntriesTabProps {
  entries: ProfitEntry[];
  totalEntries: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onEdit: (entry: ProfitEntry) => void;
  onDelete: (id: string) => void;
  onDownload: (type: "entries" | "country" | "payment" | "daily") => void;
  onAddExpense: (entry: ProfitEntry) => void;
}

export const EntriesTab: React.FC<EntriesTabProps> = ({
  entries,
  totalEntries,
  page,
  pageSize,
  onPageChange,
  onEdit,
  onDelete,
  onDownload,
  onAddExpense,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));

  return (
    <div className="p-6 bg-white rounded-xl border">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Toutes les entrées ({totalEntries})</h2>
        <div className="flex gap-2">
          <button
            onClick={() => onDownload("entries")}
            className="flex gap-1 items-center px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg border hover:bg-gray-200"
          >
            <Download size={14} /> Excel
          </button>
        </div>
        <div className="flex gap-2 items-center text-sm text-gray-500">
          <button
            disabled={page === 0}
            onClick={() => onPageChange(page - 1)}
            className="px-3 py-1 rounded-lg border disabled:opacity-40 hover:bg-gray-50"
          >
            Précédent
          </button>
          <span>
            Page {page + 1} / {totalPages}
          </span>
          <button
            disabled={(page + 1) * pageSize >= totalEntries}
            onClick={() => onPageChange(page + 1)}
            className="px-3 py-1 rounded-lg border disabled:opacity-40 hover:bg-gray-50"
          >
            Suivant
          </button>
        </div>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-gray-400">Aucune entrée</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Heure</th>
                <th className="px-3 py-2 text-left">Direction</th>
                <th className="px-3 py-2 text-left">Pays</th>
                <th className="px-3 py-2 text-left">Paiement</th>
                <th className="px-3 py-2 text-right">Capital</th>
                <th className="px-3 py-2 text-right">Taux Achat</th>
                <th className="px-3 py-2 text-right">USDT</th>
                <th className="px-3 py-2 text-right">Taux Vente</th>
                <th className="px-3 py-2 text-right">Mnt Vente</th>
                <th className="px-3 py-2 text-right">Taux IZI</th>
                <th className="px-3 py-2 text-right">Mnt IZI</th>
                <th className="px-3 py-2 text-right">Bén. Brut</th>
                <th className="px-3 py-2 text-right">Frais</th>
                <th className="px-3 py-2 text-right">Bén. Net</th>
                <th className="px-3 py-2 text-right">Dépenses</th>
                <th className="px-3 py-2 text-right">Bén. CAD</th>
                <th className="px-3 py-2 text-right">Bén. XAF</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b hover:bg-gray-50">
                  <td className="px-3 py-2">{e.date}</td>
                  <td className="px-3 py-2">{e.heure || "—"}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        e.direction === "CAD_TO_XAF" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"
                      }`}
                    >
                      {e.direction === "CAD_TO_XAF" ? "CAD→XAF" : "XAF→CAD"}
                    </span>
                  </td>
                  <td className="px-3 py-2">{e.country}</td>
                  <td className="px-3 py-2">
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-orange-100 text-orange-700 rounded">
                      {e.payment_method}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{formatNumber(e.capital_amount)}</td>
                  <td className="px-3 py-2 text-right">{formatNumber(e.buy_rate, 4)}</td>
                  <td className="px-3 py-2 text-right">{formatNumber(e.usdt_obtained, 2)}</td>
                  <td className="px-3 py-2 text-right">{formatNumber(e.sell_rate, 2)}</td>
                  <td className="px-3 py-2 text-right">{formatNumber(e.sale_amount)}</td>
                  <td className="px-3 py-2 text-right">{formatNumber(e.izi_rate, 0)}</td>
                  <td className="px-3 py-2 text-right">{formatNumber(e.izi_amount)}</td>
                  <td className="px-3 py-2 text-right text-green-600">{formatNumber(e.gross_profit)}</td>
                  <td className="px-3 py-2 text-right text-red-500">{formatNumber(e.fees)}</td>
                  <td className="px-3 py-2 font-semibold text-right text-emerald-600">{formatNumber(e.net_profit)}</td>
                  {(() => {
                    // Recalcule à l'affichage : Bén. CAD = Revenu CAD − Dépenses (en CAD)
                    const { final_profit, final_profit_cad: benCad } = computeFinalProfits(
                      e.net_profit ?? 0,
                      e.profit_cad ?? 0,
                      e.expense_amount ?? 0,
                      e.expense_currency ?? undefined,
                      e.izi_rate ?? 0,
                      e.direction || "CAD_TO_XAF"
                    );
                    const rate = e.izi_rate ?? 0;
                    const benXaf = e.direction === "CAD_TO_XAF" ? final_profit : final_profit * rate;
                    const expenseDisplay = e.expense_amount ?? 0;
                    const { cad: expenseCad } = expenseToCadXaf(expenseDisplay, e.expense_currency ?? undefined, rate);
                    return (
                      <>
                        <td className="px-3 py-2 text-right text-orange-600">
                          {expenseDisplay > 0 ? `${formatNumber(expenseDisplay)} ${e.expense_currency || ""}` : "—"}
                        </td>
                        <td
                          className={`px-3 py-2 font-semibold text-right ${
                            benCad < 0 ? "text-red-600" : "text-purple-600"
                          }`}
                          title={`Bén. CAD = Revenu CAD − Dépenses CAD (${formatNumber(e.profit_cad ?? 0, 4)} − ${formatNumber(expenseCad, 4)})`}
                        >
                          {formatNumber(benCad, 4)}
                        </td>
                        <td
                          className={`px-3 py-2 font-semibold text-right ${
                            benXaf < 0 ? "text-red-600" : "text-blue-600"
                          }`}
                          title="Bénéfice XAF après dépenses (via Taux IZI)"
                        >
                          {formatNumber(benXaf, 0)}
                        </td>
                      </>
                    );
                  })()}
                  <td className="px-3 py-2 text-center">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => onAddExpense(e)}
                        className="p-1 text-green-400 rounded hover:text-green-600 hover:bg-green-50"
                        title="Dépenses"
                      >
                        <DollarSign size={14} />
                      </button>
                      <button
                        onClick={() => onEdit(e)}
                        className="p-1 text-blue-400 rounded hover:text-blue-600 hover:bg-blue-50"
                        title="Modifier"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => onDelete(e.id)}
                        className="p-1 text-red-400 rounded hover:text-red-600 hover:bg-red-50"
                        title="Supprimer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
